import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

const FALLBACK_MIN_HEIGHT = 360;
const CONTENT_LOADED_THRESHOLD = 200;

/**
 * Stores the content height when the audience modal table has data,
 * and returns it as minHeight so the modal doesn't jump during filter/loading.
 */
export function useStoredContentHeight(isActive: boolean): {
  minHeight: number;
  ref: RefObject<HTMLDivElement | null>;
} {
  const ref = useRef<HTMLDivElement | null>(null);
  const [minHeight, setMinHeight] = useState(FALLBACK_MIN_HEIGHT);

  useEffect(() => {
    if (!isActive || !ref.current) return;

    const el = ref.current;
    let rafId = 0;
    const observer = new ResizeObserver((entries) => {
      // Defer the state update out of the observer callback. Setting state
      // here changes the observed element's `minHeight`, which the observer
      // would pick up synchronously — producing the benign but error-overlay
      // -triggering "ResizeObserver loop completed with undelivered
      // notifications". A rAF breaks that synchronous loop.
      let next = 0;
      for (const entry of entries) {
        const { height } = entry.contentRect;
        if (height >= CONTENT_LOADED_THRESHOLD) {
          next = Math.max(next, Math.ceil(height));
        }
      }
      if (!next) return;

      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setMinHeight((prev) => (next > prev ? next : prev));
      });
    });

    observer.observe(el);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [isActive]);

  return { minHeight, ref };
}
